/**
 * 逐時段格線的期間切割與數值彙總（純函式）。
 *
 * 輸入一律是「以日為粒度」的資料（`DailyValue[]`，date 為 `YYYY-MM-DD`），
 * 由本檔依所選粒度（日／週／月）切出期間並加總。用純 UTC 紀元日計算，
 * 不使用 `Date` 的當地時區運算，避免夏令時間或時區造成的離散誤差。
 */

export type TimeGranularity = 'Day' | 'Week' | 'Month';

export interface DailyValue {
  /** `YYYY-MM-DD`。 */
  readonly date: string;
  readonly hours: number;
  /** 該日是否過度分配。 */
  readonly overallocated?: boolean;
}

export interface Period {
  /** 期間起始日 `YYYY-MM-DD`（含）。 */
  readonly start: string;
  readonly label: string;
}

export interface PeriodValue {
  readonly hours: number;
  readonly overallocated: boolean;
}

const MS_PER_DAY = 86_400_000;

function toEpochDay(date: string): number {
  return Math.floor(Date.parse(`${date}T00:00:00Z`) / MS_PER_DAY);
}

function fromEpochDay(day: number): string {
  return new Date(day * MS_PER_DAY).toISOString().slice(0, 10);
}

/** ISO 週一為週首；回傳該週週一的 epoch day。 */
function startOfWeek(day: number): number {
  // 1970-01-01 是週四（weekday index 3，0=週一）。
  const weekday = ((day % 7) + 7 + 3) % 7;
  return day - weekday;
}

function startOfMonth(date: string): string {
  return `${date.slice(0, 7)}-01`;
}

function addMonths(yyyyMm: string, count: number): string {
  const [yearStr, monthStr] = yyyyMm.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const total = year * 12 + (month - 1) + count;
  const nextYear = Math.floor(total / 12);
  const nextMonth = total - nextYear * 12 + 1;
  return `${String(nextYear).padStart(4, '0')}-${String(nextMonth).padStart(2, '0')}-01`;
}

/** 依粒度把 `[rangeStart, rangeEnd]`（含端點）切成期間清單。 */
export function buildPeriods(
  rangeStart: string,
  rangeEnd: string,
  granularity: TimeGranularity,
): readonly Period[] {
  if (rangeStart > rangeEnd) return [];
  const periods: Period[] = [];

  if (granularity === 'Day') {
    let day = toEpochDay(rangeStart);
    const last = toEpochDay(rangeEnd);
    for (; day <= last; day += 1) {
      const date = fromEpochDay(day);
      periods.push({ start: date, label: date.slice(5) });
    }
    return periods;
  }

  if (granularity === 'Week') {
    let cursor = startOfWeek(toEpochDay(rangeStart));
    const last = toEpochDay(rangeEnd);
    for (; cursor <= last; cursor += 7) {
      const date = fromEpochDay(cursor);
      periods.push({ start: date, label: `W${date.slice(5)}` });
    }
    return periods;
  }

  // Month
  let cursor = startOfMonth(rangeStart);
  const lastMonth = startOfMonth(rangeEnd);
  for (; cursor <= lastMonth; cursor = addMonths(cursor, 1)) {
    periods.push({ start: cursor, label: cursor.slice(0, 7) });
  }
  return periods;
}

/** 期間的（不含）結束日，供 [start, end) 判斷用。 */
function periodEnd(period: Period, granularity: TimeGranularity): string {
  if (granularity === 'Day') return fromEpochDay(toEpochDay(period.start) + 1);
  if (granularity === 'Week') return fromEpochDay(toEpochDay(period.start) + 7);
  return addMonths(period.start, 1);
}

/**
 * 把逐日資料彙總進期間（依 `periods` 對應之區間加總）。
 * 回傳陣列與 `periods` 同長度、同順序。
 *
 * `rangeStart`／`rangeEnd`（含端點）可選：不提供時只依期間本身的 `[start, end)`
 * 邊界彙總（沿用舊行為）。提供時，彙總範圍會再夾在 `[rangeStart, rangeEnd]` 內 ——
 * 這是必要的，因為週／月期間的邊界（例如 `buildPeriods()` 對齊出的第一個週一）
 * 常常早於使用者請求的 `rangeStart`，若不夾住，頭尾兩格會把範圍外的資料也灌進來。
 */
export function aggregateByPeriod(
  daily: readonly DailyValue[],
  periods: readonly Period[],
  granularity: TimeGranularity,
  rangeStart?: string,
  rangeEnd?: string,
): readonly PeriodValue[] {
  return periods.map((period) => {
    const end = periodEnd(period, granularity);
    let hours = 0;
    let overallocated = false;
    for (const entry of daily) {
      if (entry.date < period.start || entry.date >= end) continue;
      if (rangeStart !== undefined && entry.date < rangeStart) continue;
      if (rangeEnd !== undefined && entry.date > rangeEnd) continue;
      hours += entry.hours;
      if (entry.overallocated) overallocated = true;
    }
    return { hours, overallocated };
  });
}
